// src/pages/sales/PageProformaInvoices.jsx
import { useState, useEffect } from "react";
import * as api from "../../apiService";
import { downloadPdf } from "../../apiService";
import { fmtDate } from "../../utils/dateUtils";
import { PICreateModal }  from "./PICreateModal";
import { PIDetailModal }  from "./PIDetailModal";
import { useToast } from "../../components/Toast";

const STATUS_STYLE = {
  DRAFT:    { bg: "#f3f4f6", color: "#374151" },
  SENT:     { bg: "#dbeafe", color: "#1e40af" },
  ACCEPTED: { bg: "#d1fae5", color: "#065f46" },
  REJECTED: { bg: "#fee2e2", color: "#991b1b" },
  EXPIRED:  { bg: "#fef3c7", color: "#92400e" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.DRAFT;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {status}
    </span>
  );
}

function fmt(n) {
  const num = parseFloat(n);
  if (isNaN(num)) return "—";
  return num.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function rowTotal(pi) {
  if (!pi.lines?.length) return 0;
  const sub = pi.lines.reduce((s, l) => s + parseFloat(l.quantity) * parseFloat(l.unitPrice), 0);
  return pi.vatEnabled ? sub * 1.05 : sub;
}

export function PageProformaInvoices({ myPerms, fgs, hmGrades, userName, userEmail = "" }) {
  const showToast = useToast();
  const [rows,         setRows]         = useState([]);
  const [quotations,   setQuotations]   = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [createOpen,   setCreateOpen]   = useState(false);
  const [editPi,       setEditPi]       = useState(null);
  const [detailRow,    setDetailRow]    = useState(null);

  const canCreate   = myPerms?.includes("QUOTATION_CREATE");
  const canManage   = myPerms?.includes("CRM_MANAGE");
  const displayRows = canManage ? rows : rows.filter(r => r.createdBy === userEmail);

  function load() {
    setLoading(true);
    Promise.all([
      api.fetchProformaInvoices(),
      api.fetchQuotations().catch(() => []),
      api.fetchBankAccounts().catch(() => []),
    ])
      .then(([pis, quos, banks]) => {
        setRows(pis);
        setQuotations(quos);
        setBankAccounts(banks);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function handleCreate(body) {
    const created = await api.createProformaInvoice(body);
    setRows(old => [created, ...old]);
    showToast("PI created");
    setCreateOpen(false);
  }

  async function handleUpdate(body) {
    const updated = await api.updateProformaInvoice(editPi.id, body);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailRow(updated);
    showToast("PI updated");
    setEditPi(null);
  }

  function handleEdit(pi) {
    setDetailRow(null);
    setEditPi(pi);
  }

  async function handleStatusChange(id, status) {
    const updated = await api.updateProformaInvoiceStatus(id, status);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailRow(updated);
    showToast("Status updated");
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Proforma Invoices</div>
          <div className="page-subtitle">{displayRows.length} PI{displayRows.length !== 1 ? "s" : ""}</div>
        </div>
        {canCreate && (
          <div className="page-header-right">
            <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>+ New PI</button>
          </div>
        )}
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>PI No.</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Company</th>
                <th>Curr</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "center" }}>Rev.</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: "center", color: "#aaa", padding: 24 }}>
                  No proforma invoices yet
                </td></tr>
              ) : displayRows.map(row => (
                <tr key={row.id} style={{ cursor: "pointer" }} onClick={() => setDetailRow(row)}>
                  <td style={{ fontFamily: "monospace", fontWeight: 600 }}>{row.piNumber}</td>
                  <td style={{ fontSize: 12 }}>{row.date ? fmtDate(row.date) : "—"}</td>
                  <td className="fw-600">{row.customerName}</td>
                  <td style={{ fontSize: 11 }}>
                    {row.piNumber?.startsWith("AMIP") ? "AMI — Industries" : "AMT — Trading"}
                  </td>
                  <td style={{ fontSize: 11 }}>{row.currency || "AED"}</td>
                  <td style={{ textAlign: "right", fontSize: 12, fontFamily: "monospace" }}>
                    {fmt(rowTotal(row))}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {(row.revisionCount > 0) ? (
                      <span style={{ background: "rgba(68,0,153,0.1)", color: "#440099",
                        borderRadius: 10, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>
                        {row.revisionCount}
                      </span>
                    ) : <span style={{ color: "#ccc", fontSize: 11 }}>—</span>}
                  </td>
                  <td><StatusBadge status={row.status} /></td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="btn-small" onClick={() =>
                      downloadPdf(`/api/crm/proforma-invoices/${row.id}/pdf`,
                        row.piNumber.replace(/\//g, "-") + ".pdf")
                    }>PDF</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setCreateOpen(false); }}>
          <PICreateModal
            fgs={fgs}
            hmGrades={hmGrades}
            userName={userName}
            quotations={quotations}
            bankAccounts={bankAccounts}

            onSave={handleCreate}
            onCancel={() => setCreateOpen(false)}
          />
        </div>
      )}

      {detailRow && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setDetailRow(null); }}>
          <PIDetailModal
            pi={detailRow}
            canCreate={canCreate}
            bankAccounts={bankAccounts}
            onStatusChange={handleStatusChange}
            onEdit={handleEdit}
            onClose={() => setDetailRow(null)}
          />
        </div>
      )}

      {editPi && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setEditPi(null); }}>
          <PICreateModal
            fgs={fgs}
            hmGrades={hmGrades}
            userName={userName}
            quotations={quotations}
            bankAccounts={bankAccounts}

            initialPi={editPi}
            onSave={handleUpdate}
            onCancel={() => setEditPi(null)}
          />
        </div>
      )}
    </>
  );
}
